import {
  OperationLog,
  OperationLogSource,
  OperationLogStatus,
} from '@/client/operation-log';
import { LoadingStateType } from '@/common/constants';
import {
  AdSyncError,
  extractUserIdentifier,
  normalizeErrorMessage,
  PENDING_DELETION_CONFLICT_CODE,
  USER_ERROR_CODES,
} from '@/pages/accounts/account-errors/error-log.helpers';
import {
  ActionRow,
  Banner,
  BannerIcon,
  BannerText,
  BannerTitle,
  CategoryTitle,
  EmptyStateCard,
  EmptyStateContent,
  ErrorCategory,
  ErrorTable,
  InlineLink,
  SectionTitle,
  TableLog,
} from '@/pages/accounts/account-errors/error-log.styled';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import React, { useMemo } from 'react';

type SyncEntry = {
  user: string;
  message: string;
  params?: unknown[];
};

const SOURCE_COPY: Record<
  string,
  {
    sectionTitle: string;
    success: string;
    completed: string;
    resolveHint: string;
  }
> = {
  [OperationLogSource.AD_SYNC]: {
    sectionTitle: 'errorLog.adErrors',
    success: 'errorLog.adSyncSuccess',
    completed: 'errorLog.adSyncCompleted',
    resolveHint: 'errorLog.adSyncResolveHint',
  },
  [OperationLogSource.GOOGLE_WORKSPACE_SYNC]: {
    sectionTitle: 'errorLog.googleErrors',
    success: 'errorLog.googleSyncSuccess',
    completed: 'errorLog.googleSyncCompleted',
    resolveHint: 'errorLog.googleSyncResolveHint',
  },
};

type Props = {
  log: OperationLog | undefined;
  source: OperationLogSource;
  t: (key: string, params?: Record<string, unknown>) => string;
  onSync: () => void;
  syncState: LoadingStateType;
  onGoToSettings: () => void;
  onGoToPendingDeletion: () => void;
};

export const SyncErrorsSection: React.FC<Props> = ({
  log,
  source,
  t,
  onSync,
  syncState,
  onGoToSettings,
  onGoToPendingDeletion,
}) => {
  const copy = SOURCE_COPY[source] ?? SOURCE_COPY[OperationLogSource.AD_SYNC];
  const errors = (log?.errors ?? []) as AdSyncError[];
  const hasErrors =
    log && log.status !== OperationLogStatus.SUCCESS && errors.length > 0;

  const groupedByType = useMemo(() => {
    const groups: Record<string, SyncEntry[]> = {};

    errors.forEach((error) => {
      if (Array.isArray(error)) {
        const [message, errorCode, params] = error as [
          string,
          string,
          unknown[],
        ];
        const label = errorCode || 'general';
        const userIdentifier = extractUserIdentifier(label, params);

        if (!groups[label]) {
          groups[label] = [];
        }

        groups[label].push({
          user: userIdentifier,
          message: String(message),
          params,
        });
      } else if (typeof error === 'string') {
        const label = 'general';

        if (!groups[label]) {
          groups[label] = [];
        }

        groups[label].push({ user: '-', message: error });
      }
    });

    return groups;
  }, [errors]);

  const getErrorTypeLabel = (errorCode: string): string => {
    const labelMap: Record<string, string> = {
      'user.already.assigned.to.branch': t(
        'errorLog.adSync.alreadyAssignedToBranch',
      ),
      'user.already.exists.in.another.account': t(
        'errorLog.adSync.alreadyExistsInAnotherAccount',
      ),
      'user.no.email.provided': t('errorLog.adSync.noEmailProvided'),
      'user.duplicate.username': t('errorLog.adSync.duplicateUsername'),
      [PENDING_DELETION_CONFLICT_CODE]: t(
        'errorLog.sync.pendingDeletionConflict',
      ),
      general: t('errorLog.adSync.general'),
      '404': t('errorLog.adSync.groupNotFound'),
    };

    return labelMap[errorCode] || errorCode;
  };

  const renderErrorCell = (errorCode: string, entry: SyncEntry) => {
    if (errorCode === PENDING_DELETION_CONFLICT_CODE) {
      const [incomingName, username, pendingName] = (entry.params ??
        []) as string[];

      return (
        <>
          {t('errorLog.sync.pendingDeletionConflictMessage', {
            username,
            pendingName,
            incomingName,
          })}{' '}
          <InlineLink type="button" onClick={onGoToPendingDeletion}>
            {t('errorLog.goToPendingDeletionList')}
          </InlineLink>
        </>
      );
    }

    if (USER_ERROR_CODES.includes(errorCode)) {
      return getErrorTypeLabel(errorCode);
    }

    return normalizeErrorMessage(entry.message);
  };

  return (
    <FlexContainer direction="column" gap={12} align="flex-start">
      <SectionTitle>{t(copy.sectionTitle)}</SectionTitle>

      {!hasErrors && (
        <>
          <Banner variant="success">
            <BannerIcon variant="success">
              <i className="pi pi-check" />
            </BannerIcon>
            <BannerText>
              <BannerTitle>{t(copy.success)}</BannerTitle>
            </BannerText>
          </Banner>
          <EmptyStateCard>
            <EmptyStateContent>
              <i className="pi pi-check-circle" />
              <span>{t('errorLog.noCurrentErrors')}</span>
            </EmptyStateContent>
          </EmptyStateCard>
        </>
      )}

      {hasErrors && (
        <>
          <Banner variant="warning">
            <BannerIcon variant="warning">
              <i className="pi pi-exclamation-circle" />
            </BannerIcon>
            <BannerText>
              <BannerTitle>
                {t(copy.completed, { count: log.errorCount })}
              </BannerTitle>
              <span>{t(copy.resolveHint)}</span>
            </BannerText>
          </Banner>

          <TableLog>
            {Object.entries(groupedByType).map(([errorCode, entries]) => {
              const isUserError = USER_ERROR_CODES.includes(errorCode);

              return (
                <ErrorCategory key={errorCode}>
                  <FlexContainer justify="space-between" align="center">
                    <CategoryTitle>
                      {getErrorTypeLabel(errorCode)} ({entries.length})
                    </CategoryTitle>
                  </FlexContainer>
                  <ErrorTable>
                    <thead>
                      <tr>
                        {isUserError && <th>{t('errorLog.user')}</th>}
                        <th>{t('errorLog.error')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry, index) => (
                        <tr key={index}>
                          {isUserError && <td>{entry.user}</td>}
                          <td>{renderErrorCell(errorCode, entry)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </ErrorTable>
                </ErrorCategory>
              );
            })}
          </TableLog>
        </>
      )}

      <ActionRow>
        <AppButton
          label={t('errorLog.rerunSync')}
          icon="pi pi-sync"
          onClick={onSync}
          state={syncState}
        />
        <AppButton
          label={t('errorLog.goToSettings')}
          type="outlined"
          onClick={onGoToSettings}
        />
      </ActionRow>
    </FlexContainer>
  );
};
